import { ISubTool, SubToolInputKind } from './SubTool';
import type { DragEventData } from './BaseTool';
import type { SelectionContextProvider } from './tools/implementations/line-tool/SelectionContextProvider';

/**
 * One hosted subtool plus the input kinds the host is currently suppressing
 * for it. Effective accepted kinds are `subtool.getInputMask()` minus `muted`.
 */
interface HostedEntry {
  subtool: ISubTool;
  muted: Set<SubToolInputKind>;
}

/**
 * Composable controller that lets any tool host one or more subtools without
 * dictating a base class — TypeScript is single-inheritance, and the host tools
 * already extend a domain base (`SurfaceToolBase` / `FloorplanToolBase`) for
 * their isolation/alignment behavior. The host tool *owns* a `SubToolHost` and
 * forwards its lifecycle + input into it.
 *
 * The controller owns:
 * - A `SelectionContextProvider` (floor or surface): attached on `activate`,
 *   detached on `deactivate`; its `BasePlaneState` is pushed to every subtool
 *   via `setBasePlane` on activate + on every selection change.
 * - Routing input to active subtools, filtered by each subtool's input mask
 *   minus any kinds the host has muted (e.g. suppress draw input during an
 *   interactive camera-align gesture). A subtool returning `true` from a
 *   handler stops propagation to lower-priority subtools.
 * - Forwarding the host's drag state so subtools see `isCameraDragging()` right.
 *
 * The host tool keeps owning DOM input + drag detection (via its own `BaseTool`
 * base) and decides when to forward each event into the controller, so it can
 * intercept first (the surface host eats pointer input during alignment).
 */
export class SubToolHost {
  private hosted: HostedEntry[] = [];

  constructor(private readonly selectionContext: SelectionContextProvider) {}

  /** Register a subtool. Registration order is routing priority. */
  addSubTool(subtool: ISubTool): void {
    this.hosted.push({ subtool, muted: new Set() });
  }

  get subtools(): readonly ISubTool[] {
    return this.hosted.map((h) => h.subtool);
  }

  /** Suppress an input kind for a subtool (host-controlled, for ordering). */
  muteSubToolInput(subtool: ISubTool, kind: SubToolInputKind): void {
    this.entryFor(subtool)?.muted.add(kind);
  }

  /** Re-enable a previously muted input kind. */
  unmuteSubToolInput(subtool: ISubTool, kind: SubToolInputKind): void {
    this.entryFor(subtool)?.muted.delete(kind);
  }

  private entryFor(subtool: ISubTool): HostedEntry | undefined {
    return this.hosted.find((h) => h.subtool === subtool);
  }

  // ─── Lifecycle (host forwards these) ────────────────────────────────

  /** Attach the selection context, activate subtools, push the current plane. */
  activate(): void {
    this.selectionContext.attach(this.pushBasePlane);
    for (const { subtool } of this.hosted) subtool.activate();
    this.pushBasePlane();
  }

  /** Deactivate subtools and detach the selection context. */
  deactivate(): void {
    for (const { subtool } of this.hosted) subtool.deactivate();
    this.selectionContext.detach();
  }

  dispose(): void {
    for (const { subtool } of this.hosted) subtool.dispose();
  }

  /** Push the current base plane to every subtool. Re-callable by the host. */
  pushBasePlane = (): void => {
    const state = this.selectionContext.getBasePlaneState();
    for (const { subtool } of this.hosted) subtool.setBasePlane(state);
  };

  /** Forward the host's drag-detection state into subtools each move. */
  setHostDragging(dragging: boolean): void {
    for (const { subtool } of this.hosted) {
      const withDrag = subtool as ISubTool & { setHostDragging?: (d: boolean) => void };
      withDrag.setHostDragging?.(dragging);
    }
  }

  // ─── Input routing (host forwards already-detected events) ──────────

  routeMouseDown(event: MouseEvent): void {
    this.route('pointer', (s) => s.onMouseDown?.(event));
  }
  routeMouseMove(event: MouseEvent): void {
    this.route('pointer', (s) => s.onMouseMove?.(event));
  }
  routeMouseUp(event: MouseEvent): void {
    this.route('pointer', (s) => s.onMouseUp?.(event));
  }
  routeClick(event: MouseEvent): void {
    this.route('click', (s) => s.onClick?.(event));
  }
  routeRightClick(event: MouseEvent): void {
    this.route('pointer', (s) => s.onRightClick?.(event));
  }
  routeKeyDown(event: KeyboardEvent): void {
    this.route('keyboard', (s) => s.onKeyDown?.(event));
  }
  routeKeyUp(event: KeyboardEvent): void {
    this.route('keyboard', (s) => s.onKeyUp?.(event));
  }
  routeWheel(event: WheelEvent): void {
    this.route('wheel', (s) => s.onWheel?.(event));
  }
  // Drag callbacks (host owns drag detection, forwards the detected drag).
  // Gated on the 'pointer' mask like the other pointer events.
  routeDragStart(data: DragEventData): void {
    this.route('pointer', (s) => s.onDragStart?.(data));
  }
  routeDrag(data: DragEventData): void {
    this.route('pointer', (s) => s.onDrag?.(data));
  }
  routeDragEnd(data: DragEventData): void {
    this.route('pointer', (s) => s.onDragEnd?.(data));
  }

  private route(kind: SubToolInputKind, run: (subtool: ISubTool) => boolean | void): void {
    for (const { subtool, muted } of this.hosted) {
      if (muted.has(kind)) continue;
      if (!subtool.getInputMask().has(kind)) continue;
      if (run(subtool) === true) return;
    }
  }
}
